import { AnimatePresence, motion } from "motion/react";
import { useState, useEffect, useRef } from "react";
import { APIs } from "../../services/Apis";
import { OnboardingGlow } from "../backgrounds/OnboardingGlow";

const TOTAL_DURATION_MS = 90000;
// Once the segments have actually arrived the remaining steps are played out
// rather than skipped, so this is the pace most of the loader is read at.
const FAST_FORWARD_STEP_MS = 3500;
// How long "Your segments are ready." holds before step 3 opens. Kept separate
// from the step pace so slowing the cycle doesn't stall the handover.
const FINISH_HOLD_MS = 2000;
const COMPLETION_THRESHOLD = 0.8;

// Generate random durations for each step that sum to approximately TOTAL_DURATION_MS
const generateRandomDurations = (count) => {
  if (count === 0) return [];
  if (count === 1) return [TOTAL_DURATION_MS];

  // Generate random weights for each step
  const weights = Array.from({ length: count }, () => Math.random() + 0.5);
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

  return weights.map((weight) =>
    Math.round((weight / totalWeight) * TOTAL_DURATION_MS),
  );
};

// The log above the headline reads as history, so drop the trailing ellipsis
// the in-progress copy carries. If the endpoint ever returns a past-tense form
// we use that instead.
const toLogLine = (message) =>
  typeof message === "string"
    ? message.replace(/[\s.…]+$/, "")
    : (message?.past ?? message?.text ?? "").replace(/[\s.…]+$/, "");

const toStatusLine = (message) =>
  typeof message === "string" ? message : (message?.text ?? "");

// Scoped so the loader doesn't depend on utility classes that only exist after
// a Tailwind rebuild of public/output.css.
const STYLES = `
.seg3-wrap{position:relative;z-index:30;width:100%;max-width:540px;margin:0 auto;padding:0 24px}
.seg3-segs{display:flex;gap:4px;margin-bottom:34px}
.seg3-segs i{flex:1;height:3px;border-radius:2px;background:#e5e7eb;position:relative;overflow:hidden}
.seg3-segs i.is-done{background:var(--color-primary)}
.seg3-segs i em{position:absolute;inset:0;display:block;background:var(--color-primary);transform-origin:left;transform:scaleX(0);animation:seg3-fill var(--seg3-fill,2600ms) linear forwards}
@keyframes seg3-fill{from{transform:scaleX(0)}to{transform:scaleX(1)}}

.seg3-past{height:150px;display:flex;flex-direction:column;justify-content:flex-end;gap:6px;overflow:hidden;-webkit-mask:linear-gradient(transparent,#000 45%);mask:linear-gradient(transparent,#000 45%)}
.seg3-ln{display:flex;align-items:center;gap:10px;font-size:13.5px;line-height:1.35;color:#6b7280;animation:seg3-rise .4s ease}
@keyframes seg3-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}

.seg3-chk{border-radius:50%;background:var(--color-primary);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;flex:none}
.seg3-ln .seg3-chk{width:14px;height:14px;font-size:8px}

.seg3-now{display:grid;grid-template-columns:36px 1fr;gap:16px;align-items:start;margin-top:22px;min-height:96px}
.seg3-ring{width:36px;height:36px;position:relative;display:flex;align-items:center;justify-content:center;margin-top:4px}
.seg3-ring::before{content:"";position:absolute;inset:0;border-radius:50%;border:2px solid rgba(77,124,15,.15)}
.seg3-ring::after{content:"";position:absolute;inset:0;border-radius:50%;border:2px solid transparent;border-top-color:var(--color-primary);animation:seg3-spin 1s linear infinite}
@keyframes seg3-spin{to{transform:rotate(360deg)}}
.seg3-ring.is-done::before,.seg3-ring.is-done::after{display:none}
.seg3-ring .seg3-chk{width:36px;height:36px;font-size:16px;display:none}
.seg3-ring.is-done .seg3-chk{display:flex}

.seg3-now h3{font-family:'Newsreader',Georgia,serif;font-weight:400;font-size:30px;letter-spacing:-.01em;margin:0;line-height:1.2;color:#111827}
.seg3-now p{margin:8px 0 0;font-size:13px;line-height:1.5;color:#9ca3af}

.seg3-foot{margin-top:40px;display:flex;justify-content:space-between;font-family:'Geist Mono','JetBrains Mono',ui-monospace,monospace;font-size:11.5px;color:#9ca3af;letter-spacing:.04em;border-top:1px solid #f3f4f6;padding-top:14px}
.seg3-foot b{color:#6b7280;font-weight:500}
`;

export const MultiStepLoaderSegments = ({
  loading,
  dataReady = false,
  onComplete,
  businessName,
  businessUrl,
  businessDescription,
  stakeholders,
}) => {
  const [messages, setMessages] = useState([]);
  const [isFetchingMessages, setIsFetchingMessages] = useState(true);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [durations, setDurations] = useState([]);
  const [fastForward, setFastForward] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const hasFetched = useRef(false);
  const timeoutRef = useRef(null);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!loading || hasFetched.current) return;

    const fetchMessages = async () => {
      try {
        hasFetched.current = true;
        const response = await APIs.getSegmentsWaitingMessages(
          businessName,
          businessUrl,
          businessDescription,
          stakeholders,
        );

        const messagesToSet =
          response && response.success && response.messages
            ? response.messages
            : [
                "Analyzing your business data...",
                "Identifying key segments...",
                "Building engagement strategies...",
              ];

        setMessages(messagesToSet);
        setDurations(generateRandomDurations(messagesToSet.length));
      } catch (error) {
        console.error("Failed to fetch waiting messages:", error);
        const fallbackMessages = [
          "Analyzing your business data...",
          "Identifying key segments...",
          "Building engagement strategies...",
        ];
        setMessages(fallbackMessages);
        setDurations(generateRandomDurations(fallbackMessages.length));
      } finally {
        setIsFetchingMessages(false);
      }
    };

    fetchMessages();
  }, [loading, businessName, businessUrl, businessDescription, stakeholders]);

  useEffect(() => {
    if (dataReady && !fastForward) {
      setFastForward(true);
    }
  }, [dataReady, fastForward]);

  useEffect(() => {
    if (
      !loading ||
      isFetchingMessages ||
      messages.length === 0 ||
      durations.length === 0
    )
      return;
    if (currentMessageIndex >= messages.length - 1) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const stepDuration = fastForward
      ? FAST_FORWARD_STEP_MS
      : durations[currentMessageIndex];

    timeoutRef.current = setTimeout(() => {
      setCurrentMessageIndex((prev) => {
        if (prev >= messages.length - 1) return prev;
        return prev + 1;
      });
    }, stepDuration);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    loading,
    isFetchingMessages,
    messages.length,
    durations,
    currentMessageIndex,
    fastForward,
  ]);

  useEffect(() => {
    if (
      !dataReady ||
      !onComplete ||
      messages.length === 0 ||
      completedRef.current
    )
      return;

    const threshold = Math.ceil(messages.length * COMPLETION_THRESHOLD);
    if (currentMessageIndex >= threshold) {
      completedRef.current = true;
      setIsFinishing(true);
      setTimeout(onComplete, FINISH_HOLD_MS);
    }
  }, [dataReady, currentMessageIndex, messages.length, onComplete]);

  useEffect(() => {
    if (!loading) {
      setCurrentMessageIndex(0);
      setMessages([]);
      setDurations([]);
      setIsFetchingMessages(true);
      setFastForward(false);
      setIsFinishing(false);
      hasFetched.current = false;
      completedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [loading]);

  const total = messages.length;
  // Everything strictly before this index has landed and moves into the log.
  const doneCount = isFinishing ? total : currentMessageIndex;
  const doneMessages = messages.slice(0, doneCount);
  const activeFillMs = fastForward
    ? FAST_FORWARD_STEP_MS
    : durations[currentMessageIndex] || 4000;

  const headline = isFinishing
    ? "Your segments are ready."
    : isFetchingMessages
      ? "Getting your business ready…"
      : toStatusLine(messages[currentMessageIndex]);

  const subline = isFinishing
    ? businessName
      ? `${total} step${total === 1 ? "" : "s"} complete for ${businessName}`
      : `${total} step${total === 1 ? "" : "s"} complete`
    : businessName
      ? `Building segments for ${businessName} · usually 60–90 seconds`
      : "Building segments · usually 60–90 seconds";

  return (
    <AnimatePresence mode="wait">
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full h-full fixed inset-0 z-[100] flex items-center justify-center bg-white"
        >
          <style>{STYLES}</style>
          {/* Same wash the steps either side of this loader use, so the
              backdrop holds still across the transition. */}
          <OnboardingGlow />

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="seg3-wrap"
          >
            {/* Segmented bar — how much is left, without listing future steps */}
            {total > 0 && (
              <div className="seg3-segs">
                {messages.map((_, index) => {
                  const isDone = index < doneCount;
                  const isActive = !isFinishing && index === currentMessageIndex;
                  return (
                    <i key={index} className={isDone ? "is-done" : undefined}>
                      {isActive && (
                        <em
                          key={`${currentMessageIndex}-${fastForward}`}
                          style={{ "--seg3-fill": `${activeFillMs}ms` }}
                        />
                      )}
                    </i>
                  );
                })}
              </div>
            )}

            {/* Finished steps, stacked as a quiet log */}
            <div className="seg3-past">
              {doneMessages.map((message, index) => (
                <div className="seg3-ln" key={index}>
                  <span className="seg3-chk">✓</span>
                  {toLogLine(message)}
                </div>
              ))}
            </div>

            {/* The step happening now */}
            <div className="seg3-now">
              <div className={`seg3-ring${isFinishing ? " is-done" : ""}`}>
                <span className="seg3-chk">✓</span>
              </div>
              <div>
                <h3>{headline}</h3>
                <p>{subline}</p>
              </div>
            </div>

            <div className="seg3-foot">
              <span>
                {total > 0 ? (
                  <>
                    step <b>{Math.min(doneCount + (isFinishing ? 0 : 1), total)}</b>{" "}
                    of {total}
                  </>
                ) : (
                  "preparing"
                )}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
